/**
 * Contextual surfacing — the pure half.
 *
 * ===========================================================================
 * WHAT THIS IS FOR
 * ===========================================================================
 * Insights is a destination: you go to it and ask. This file backs the other
 * direction — what Phibian noticed about THIS person, or THIS program, shown
 * on the page where somebody is already working.
 *
 * ===========================================================================
 * WORKERS ARE NOT A SCOPE HERE, AND THE OMISSION IS THE POINT
 * ===========================================================================
 * `OPERATIONAL-LIBRARY-V1.md` §2.1 — "Management sees intelligence; workers
 * see priorities." A finding reaches a worker as the TASK it produced, never
 * as the finding. So the scopes below are the two MANAGEMENT surfaces — a
 * manager reading one person's record, and a manager reading one program —
 * and there is deliberately no scope that a worker's own view could pass.
 * `worker` here means "the worker this manager is looking at", which is why
 * the field is `workerId` and not `me`.
 *
 * ===========================================================================
 * THE PANEL MUST NOT BECOME AN EXISTENCE ORACLE
 * ===========================================================================
 * Two rules, and both of them are why these sentences are functions rather
 * than string literals in JSX:
 *
 *   1. NO SENTENCE MAY CLAIM A ZERO IT DID NOT MEASURE. `emptyLine` never
 *      says "this person has nothing"; it says nothing was noticed THAT YOU
 *      CAN SEE, and when the server told us the answer was narrowed it says
 *      so out loud. The reach rules hide a finding whose subject the reader
 *      cannot reach, so "none" and "none for you" are genuinely different
 *      claims and only the second one is ours to make.
 *   2. EVERY NUMBER COMES OFF THE RESPONSE. `total` is narrowed by the reach
 *      predicate inside the same WHERE clause that pages the list
 *      (`repository.ts:listFindings`), so it is the caller's total. Nothing
 *      here reconstructs a count from a permission set.
 */

import type { FindingState } from './insightsClient.js';

/**
 * The states a contextual panel shows: the ones where the condition is still
 * live. Resolved and dismissed findings are history and belong on Insights,
 * not beside somebody's schedule — a panel that listed them would grow without
 * bound and would read as a rap sheet.
 *
 * Mirrors `OPEN_FINDING_STATES` in the API's `findings/contract.ts`.
 */
export const OPEN_STATES: readonly FindingState[] = ['needs_attention', 'watching'];

/** How many rows a contextual panel shows before it defers to Insights. */
export const PANEL_LIMIT = 5;

/**
 * Which page is asking. One variant per surface that was CHOSEN, so a surface
 * cannot be added by passing a string — adding one means adding a variant
 * here, and that is a place a reviewer looks.
 */
export type FindingScope =
  | { readonly kind: 'worker'; readonly workerId: string; readonly who: string }
  | { readonly kind: 'program'; readonly programId: string; readonly what: string };

export function scopeId(scope: FindingScope): string {
  return scope.kind === 'worker' ? scope.workerId : scope.programId;
}

/**
 * The line under the panel heading.
 *
 * `organizationWide` is TRI-STATE and all three branches are written out.
 * `undefined` is "not yet known", and it must not borrow either of the other
 * two sentences: while the read is in flight, telling a supervisor they are
 * seeing everything is a claim nobody made.
 */
export function frameLine(scope: FindingScope, organizationWide: boolean | undefined): string {
  const subject =
    scope.kind === 'worker'
      ? `What Phibian noticed about ${scope.who}`
      : `What Phibian noticed in ${scope.what}`;
  if (organizationWide === undefined) {
    return `${subject}, in the records your account can see. We could not check how far that reaches.`;
  }
  if (organizationWide) return `${subject}.`;
  return `${subject}, among the people you supervise. Someone with organization-wide access may see more.`;
}

/**
 * The empty state, which is the sentence this whole file exists to get right.
 *
 * It is NOT "nothing to see here" and it is NOT `SuccessEmpty`'s "you're all
 * caught up". Findings are the output of a sweep over detectors that do not
 * yet cover every domain, and the panel cannot tell "the detectors looked and
 * found nothing" from "no detector looks at this". So it says what is true —
 * nothing OPEN has been noticed — and never converts that into a clean bill
 * of health.
 */
export function emptyLine(scope: FindingScope, organizationWide: boolean | undefined): string {
  const head =
    scope.kind === 'worker'
      ? 'Nothing is currently open about this person.'
      : 'Nothing is currently open in this program.';
  if (organizationWide === false) {
    return `${head} You are seeing the people you supervise; someone with organization-wide access may see more.`;
  }
  if (organizationWide === undefined) {
    return `${head} We could not check how far your access reaches.`;
  }
  return head;
}

/**
 * "…and N more", or nothing.
 *
 * `total` is the server's reach-narrowed total for THIS filter, and `shown` is
 * how many rows the panel rendered. Returning `null` rather than an empty
 * string keeps the caller from rendering an empty element that reads as a
 * missing sentence.
 */
export function moreLine(total: number | undefined, shown: number): string | null {
  if (total === undefined) return null;
  const rest = total - shown;
  if (rest <= 0) return null;
  return rest === 1 ? '1 more is open' : `${rest} more are open`;
}

/**
 * Where "see all" goes. The contextual panel is a doorway to Insights, not a
 * second Insights: everything it cannot show — resolved history, dismissals
 * and their reasons, evidence, the full filter row — is one link away rather
 * than reimplemented in a card.
 *
 * The worker scope has NO Insights filter to hand off to: `/insights` filters
 * by program, site, owner and severity, and there is no worker filter on that
 * screen. Sending a reader to an unfiltered queue and calling it "all findings
 * for this person" would be a lie in a link, so the worker panel links to the
 * queue plainly and says so.
 */
export function allInsightsHref(scope: FindingScope): string {
  return scope.kind === 'program'
    ? `/insights?view=needs_attention&program=${encodeURIComponent(scope.programId)}`
    : '/insights';
}

export function allInsightsLabel(scope: FindingScope): string {
  return scope.kind === 'program' ? 'All findings in this program →' : 'Open Insights →';
}
